import React, { useCallback, useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { WidgetError } from "@/components/WidgetError";
import { fmtMoney } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Edit3 } from "lucide-react";
import RateEditor from "./_shared/RateEditor";

/**
 * Default service rates page. Only shows and manages default rates —
 * i.e. rate rules that do NOT have a customer_account_id.
 *
 * Customer-specific rates are managed exclusively on the Customer Detail
 * page via CustomerRatesCard.
 */
export default function RatesDefaultPage() {
  const [rates, setRates] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, s] = await Promise.all([
        api.get("/finance/rate-rules", { params: { limit: 100 } }),
        api.get("/service-types", { params: { limit: 100 } }),
      ]);
      setRates(r.data.items || []);
      setServices(s.data.items || []);
    } catch (e) { setError(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const svcLabel = (id) =>
    services.find((s) => s.id === id)?.label || id?.slice(0, 8) || "—";

  const del = async (id) => {
    if (!window.confirm("Deactivate this default rate? Historic invoices stay unchanged.")) return;
    try {
      await api.delete(`/finance/rate-rules/${id}`);
      toast.success("Rate deactivated");
      load();
    } catch (e) { toast.error(formatError(e, "Failed")); }
  };

  if (error) {
    return <WidgetError error={error} onRetry={load}
                        title="Could not load rates" testId="rates-error"/>;
  }

  // Only default rates on this page — customer-specific rates are managed
  // from the Customer Detail page.
  const defaults = rates.filter((r) => !r.customer_account_id && r.is_active);
  const inactive = rates.filter((r) => !r.customer_account_id && !r.is_active);

  return (
    <div data-testid="rates-page">
      <div className="flex items-center gap-3 mb-2">
        <h1 className="text-3xl font-black tracking-tight">Default Service Rates</h1>
        <Button onClick={() => { setEditing(null); setShowAdd(true); }}
                data-testid="rates-add-btn"
                className="rounded bg-slate-900 hover:bg-slate-800 h-9">
          <Plus size={14} className="mr-2"/> Add default rate
        </Button>
      </div>
      <p className="text-sm text-slate-600 mb-6">
        Default rates apply to every customer. To create a rate for a specific
        customer, open that customer&apos;s detail page and use the
        <b> Customer-specific rates</b> card. Historic invoices always keep the
        rate that was used at the time.
      </p>

      {loading ? (
        <div className="label-overline text-slate-500">Loading…</div>
      ) : (
        <>
          <RatesTable title="Default rates" testId="rates-defaults"
                      rows={defaults} svcLabel={svcLabel}
                      onEdit={(r) => { setEditing(r); setShowAdd(true); }}
                      onDelete={del} />
          {inactive.length > 0 && (
            <>
              <div className="mt-6" />
              <RatesTable title="Inactive defaults" testId="rates-inactive"
                          rows={inactive} svcLabel={svcLabel}
                          onEdit={null} onDelete={null} />
            </>
          )}
        </>
      )}

      <RateEditor open={showAdd} onClose={() => setShowAdd(false)}
                  onSaved={() => { setShowAdd(false); load(); }}
                  services={services} editing={editing} />
    </div>
  );
}

function RatesTable({ title, testId, rows, svcLabel, onEdit, onDelete }) {
  return (
    <div className="bg-white border border-slate-200 rounded" data-testid={testId}>
      <div className="px-4 py-3 border-b border-slate-200 label-overline flex items-center justify-between">
        <span>{title}</span>
        <span className="text-[10px] font-mono text-slate-500">{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div className="p-6 text-center text-xs text-slate-500">No rates</div>
      ) : (
        <table className="tbl w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 label-overline text-slate-600">
            <tr>
              <th className="text-left px-3 py-2">Code</th>
              <th className="text-left px-3 py-2">Service</th>
              <th className="text-left px-3 py-2">Unit</th>
              <th className="text-right px-3 py-2">Price</th>
              <th className="text-left px-3 py-2">Effective from</th>
              {(onEdit || onDelete) && <th className="w-24 px-3 py-2"></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100"
                  data-testid={`rate-row-${r.code}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.code}</td>
                <td className="px-3 py-2">{svcLabel(r.service_type_id)}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.unit || "—"}</td>
                <td className="px-3 py-2 font-mono text-right">{fmtMoney(r.unit_price_cents)}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.effective_from || "—"}</td>
                {(onEdit || onDelete) && (
                  <td className="px-3 py-2 flex gap-1 justify-end">
                    {onEdit && (
                      <button onClick={() => onEdit(r)}
                              data-testid={`rate-edit-${r.code}`}
                              className="p-1 rounded hover:bg-slate-100 text-slate-600">
                        <Edit3 size={14}/>
                      </button>
                    )}
                    {onDelete && (
                      <button onClick={() => onDelete(r.id)}
                              data-testid={`rate-del-${r.code}`}
                              className="p-1 rounded hover:bg-red-50 text-red-600">
                        <Trash2 size={14}/>
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
